import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Key, User, Eye, EyeOff, LogIn, ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

function SalonPartnerLogin() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!identifier || !password) {
      toast.error('Phone/ID aur password dono bharo');
      return;
    }

    setLoading(true);
    try {
      // Verify credentials from Firestore via API
      const response = await fetch('/api/partner/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: identifier.trim(),
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Login failed. Check your ID/phone and password.');
        setLoading(false);
        return;
      }

      // Save to localStorage for session
      localStorage.setItem('userRole', 'salon');
      localStorage.setItem('partnerStatus', 'active');
      localStorage.setItem('partnerPhone', data.phone || identifier);
      localStorage.setItem('partnerUniqueId', data.unique_id || identifier);
      localStorage.setItem('partnerName', data.name || '');
      localStorage.setItem('firebaseUid', data.firebase_uid || '');
      localStorage.setItem('salonId', data.salon_id || '');

      // Tell App.js to re-read session flags BEFORE navigating,
      // otherwise the route guard runs with stale values and bounces us back.
      window.dispatchEvent(new Event('session-change'));

      toast.success('Login successful! 🎉');
      // Small delay so the guard re-renders with fresh state first
      setTimeout(() => navigate('/salon/dashboard'), 60);
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Network error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 py-6 sm:py-12 px-3 sm:px-4 flex items-center justify-center">
      <SEO
        title="Salon Partner Login"
        description="Salon partner login for BookYourSalons dashboard. Manage bookings, hours and services."
        path=""
        noindex
      />
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Logo size="default" className="justify-center" />
        </div>

        <Card className="border-0 shadow-xl rounded-2xl overflow-hidden">
          <div className="h-1.5 w-full bg-gradient-to-r from-purple-600 to-violet-500" />
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <LogIn className="w-5 h-5 text-purple-600" />
              Salon Partner Login
            </CardTitle>
            <CardDescription>
              Phone number ya Unique ID se login karo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Phone Number ya Unique ID
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="login-identifier-input"
                    className="pl-10"
                    placeholder="9876543210 ya UNIQUE_ID"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Password</label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    className="pl-10 pr-10"
                    placeholder="Apna password daalo"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-700 rounded-xl py-5 font-bold"
                disabled={loading}
                data-testid="partner-login-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Logging in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" /> Login
                  </span>
                )}
              </Button>

              <div className="text-center pt-2">
                <p className="text-sm text-gray-600">
                  Account nahi hai?{' '}
                  <Link to="/" className="text-purple-600 hover:underline font-medium">
                    Register as Salon Partner
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SalonPartnerLogin;
